// =============================================================================
// io/share/tradeRead.ts — the one extra read a share needs.
// -----------------------------------------------------------------------------
// The tabs that can be shared render from `TradeTableRow`, which carries 23 of
// the 48 columns. A shared report describes six fields that projection does not
// hold: the planned stop and target, the market regime, the news event, the
// mood, and the screenshot filenames.
//
// ── ⚠ WHY NOT `loadFullTrades` ──
//
// That one exists to HYDRATE — it registers every row it reads in the ledger, so
// those trades become saveable. A share is a read-only publication and has no
// business licensing writes; a mistake downstream would then destroy columns
// this read never asked for. So this is its own narrow select, and its rows are
// a shape `tradeToDb` cannot accept.
//
// ⚠ NARROW ON PURPOSE, and the list is the allow-list's twin: a column that is
// not read cannot be published by accident. `select('*')` here would put the
// per-fill tape, the voice notes and `risk_at_entry` one careless spread away
// from a public payload.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import type { ShareSourceTrade } from '../../domain/share';

/** What a share reads about one trade. ⚠ Not a `Trade`, and must never become one. */
export interface ShareTradeRow extends ShareSourceTrade {
  accountId: string | null;
  /** Both dates, because the session rule decides which day the trade is on. */
  date: string;
  sessionDate: string | null;
  /** Filenames in the `screenshots` bucket. Copied in only when the sharer opts in. */
  screenshots: string[];
}

/**
 * The columns, by name.
 *
 * ⚠ ABSENT BY CONSTRUCTION: `executions` / `extras`, `recordings`,
 * `risk_at_entry`, `source_account`. Those are the four things `domain/share.ts`
 * says never travel, and not reading them is the strongest version of that rule
 * — the allow-list downstream then has nothing to leak even if it were wrong.
 *
 * ⚠ `extras->tags` IS read, aliased, because custom tags are resolved to names
 * and colours in the payload. It is a sub-path rather than the whole `extras`
 * blob, so the import stamp and `mergedFrom` stay out.
 */
const SHARE_SELECT = [
  'id', 'account_id', 'date', 'session_date', 'time_in', 'time_out',
  'contract', 'side', 'qty', 'entry', 'exit', 'points', 'net_pnl',
  // ⚠ The three the app's own table cell renderer needs, and nothing more:
  // Gross P&L and Commissions are columns on the layout the Day Overview shows,
  // and `synthetic_close` drives the `syn` tag beside the symbol.
  'gross_pnl', 'fees', 'synthetic_close',
  'risk_points', 'rr_planned', 'rr_realized', 'planned_stop', 'planned_target',
  'mae_points', 'mfe_points',
  'pattern_id', 'exit_model_id', 'grade', 'session_label', 'market_regime',
  'news_event', 'mood', 'mistake_tags', 'booleans', 'notes', 'annotated',
  'screenshots',
  'tags:extras->tags',
].join(',');

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): number | null =>
  (v == null || v === '' ? null : (Number.isFinite(Number(v)) ? Number(v) : null));
const list = (v: unknown): string[] =>
  (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

/**
 * Read the shareable fields of specific trades.
 *
 * ⚠ RETURNED IN THE ORDER ASKED FOR, not the order the database returns. The
 * report above the table is in the table's order, and the visitor's table is
 * built from this list — a set that came back re-sorted would publish the same
 * trades under a different reading order than the picture above them.
 */
export async function loadTradesForShare(
  sb: SupabaseClient,
  ids: readonly string[],
): Promise<ShareTradeRow[]> {
  if (!ids.length) return [];
  const byId = new Map<string, ShareTradeRow>();

  const CHUNK = 200;
  for (let i = 0; i < ids.length; i += CHUNK) {
    const slice = ids.slice(i, i + CHUNK);
    const { data, error } = await sb.from('trades').select(SHARE_SELECT).in('id', slice);
    if (error) throw new Error(`Could not read the trades for this share: ${error.message}`);
    for (const raw of data ?? []) {
      // ⚠ Through `unknown`: PostgREST types an aliased sub-path row as a union
      // with its own error shape, and a direct cast is refused rather than
      // silently accepted — which is the compiler being right.
      const r = raw as unknown as Record<string, unknown>;
      const id = String(r['id'] ?? '');
      if (!id) continue;
      byId.set(id, {
        id,
        accountId: r['account_id'] == null ? null : String(r['account_id']),
        date: str(r['date']),
        sessionDate: r['session_date'] == null ? null : str(r['session_date']),
        timeIn: r['time_in'] == null ? null : str(r['time_in']),
        timeOut: r['time_out'] == null ? null : str(r['time_out']),
        contract: str(r['contract']),
        side: str(r['side']),
        qty: num(r['qty']),
        entry: num(r['entry']),
        exit: num(r['exit']),
        points: num(r['points']),
        netPnl: num(r['net_pnl']),
        grossPnl: num(r['gross_pnl']),
        fees: num(r['fees']),
        syntheticClose: r['synthetic_close'] === true,
        riskPoints: num(r['risk_points']),
        rrPlanned: num(r['rr_planned']),
        rrRealized: num(r['rr_realized']),
        plannedStop: num(r['planned_stop']),
        plannedTarget: num(r['planned_target']),
        maePoints: num(r['mae_points']),
        mfePoints: num(r['mfe_points']),
        patternId: r['pattern_id'] == null ? null : String(r['pattern_id']),
        exitModelId: r['exit_model_id'] == null ? null : String(r['exit_model_id']),
        grade: str(r['grade']),
        sessionLabel: str(r['session_label']),
        marketRegime: str(r['market_regime']),
        newsEvent: str(r['news_event']),
        mood: str(r['mood']),
        mistakeTags: list(r['mistake_tags']),
        tags: list(r['tags']),
        booleans: (r['booleans'] && typeof r['booleans'] === 'object'
          ? r['booleans'] : {}) as Record<string, boolean>,
        notes: str(r['notes']),
        // ⚠ `!== false`, so a row whose column is NULL reads as reviewed — the
        // same rule the rest of the app uses. Only an explicit false means
        // "never opened".
        annotated: r['annotated'] !== false,
        screenshots: list(r['screenshots']),
      });
    }
  }

  const out: ShareTradeRow[] = [];
  for (const id of ids) {
    const row = byId.get(String(id));
    // ⚠ A row that did not come back is DROPPED, not faked. It was deleted in
    // another tab, or RLS refused it; either way publishing a blank trade is
    // worse than publishing one fewer.
    if (row) out.push(row);
  }
  return out;
}
